/**
 * Products Landing - tabbed layout for all product management pages.
 *
 * Tabs: Library · Templates · Host Templates · Customers · Transactions · Categories · Rules · Pricing · Wizard
 * The page heading already reads "Products", so tab labels drop the redundant
 * prefix. Follows the Settings page pattern (Tab + Outlet).
 */

import { useMemo } from 'react'
import { AppShell } from '@/admin/components/app-shell'
import { Tab } from '@/shared/components/ui/tab'
import { Outlet } from 'react-router'
import { useFeatureFlag, FLAGS } from '@/admin/hooks/use-feature-flag'
import { useEntityTypeByKey } from '@/admin/hooks/use-entities'

const baseTabs = [
  { label: 'Library', path: '/manage/products/library' },
  { label: 'Templates', path: '/manage/products/templates' },
  { label: 'Host Templates', path: '/manage/products/host-templates' },
  { label: 'Customers', path: '/manage/products/customers' },
  { label: 'Transactions', path: '/manage/products/transaction-templates' },
  { label: 'Categories', path: '/manage/products/categories' },
  { label: 'Rules', path: '/manage/products/filtering' },
]

export function ProductsLandingPage() {
  const brandsEnabled = useFeatureFlag(FLAGS.BRANDS)
  // Parts (called "Components" for OC) are a tenant-defined entity. Surface the
  // tab only when the tenant has the `part` entity, and label it from the
  // entity's own per-tenant label so OC reads "Components".
  const { data: partEntity } = useEntityTypeByKey('part')
  // Pricing (WPS-162): geozone material/labor pricing. Entity-presence gated -
  // only tenants that have the pricing entity see the tab (no feature flag).
  const { data: pricingEntity } = useEntityTypeByKey('material_pricing_rate')

  const tabs = useMemo(() => {
    const t = [...baseTabs]
    if (brandsEnabled) {
      // Insert Brands directly after Categories so the catalogue
      // configuration tabs sit together.
      const idx = t.findIndex((tab) => tab.path === '/manage/products/categories')
      t.splice(idx + 1, 0, { label: 'Brands', path: '/manage/products/brands' })
    }
    if (partEntity) {
      t.push({ label: `${partEntity.label}s`, path: '/manage/products/parts' })
    }
    // Pricing prices the catalogue above it, so it follows the catalogue tabs.
    if (pricingEntity) {
      t.push({ label: 'Pricing', path: '/manage/products/pricing' })
    }
    // Geo Zones moved to Manage > Geography (WPS-222).
    t.push({ label: 'Wizard', path: '/manage/products/wizard' })
    return t
  }, [brandsEnabled, partEntity, pricingEntity])

  return (
    <AppShell>
      <main className="page-container py-10">
        <h1 className="clara-title mb-6">Products</h1>
        <Tab tabs={tabs} />
        <Outlet />
      </main>
    </AppShell>
  )
}
